import { Calendar as CalendarIcon, Filter, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Button } from '../../../components/ui/button.js';
import { Calendar } from '../../../components/ui/calendar.js';
import { Label } from '../../../components/ui/label.js';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover.js';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../../../components/ui/sheet.js';
import { Slider } from '../../../components/ui/slider.js';
import { Switch } from '../../../components/ui/switch.js';
import { Textarea } from '../../../components/ui/textarea.js';
import { cn } from '../../../lib/utils.js';
import type { ScenarioRow } from '../types.js';

/**
 * Side-drawer power filter. Mirrors
 * `wf-audit/src/slices/scenario/components/scenarioFilter/Provider.tsx`
 * — the Filter button next to the search input opens a Radix `Sheet`
 * with the rich filter set the legacy carried in its
 * `useFilterStore` Zustand slice.
 *
 * Today's filter set (chosen for highest-utility-per-implementation):
 *   • Modules range (min/max slider)
 *   • Operations range (min/max slider — `lastSuccessfulRunOperations`)
 *   • Modified by (multi-select against the distinct list of
 *     `modifiedBy` values present in the loaded scenarios)
 *   • Applications (multi-select against the distinct `type` slugs
 *     present across `applications[].type`)
 *   • Tags (multi-select; keyed by tag ID so a rename in the
 *     governance page doesn't drop the filter, with `title` carried
 *     alongside for the chip label)
 *
 * Date-range filters (last-run / last-modified / scheduled) are
 *  deferred — they require a calendar primitive + filter store
 * wiring that's out of scope for stage 3a.
 */

/**
 * Single-date comparator filter shape — mirrors legacy
 * `IScenarioFilterSimpleDateRange` (`{ enabled, type, date }`).
 * `date` is ISO calendar-day `YYYY-MM-DD`; absent value = inactive
 * for that comparator slot.
 */
export type DateComparator = 'before' | 'on' | 'after';
export interface DateFilter {
  comparator: DateComparator;
  date: string;
}

export interface ScenarioFilterValue {
  modules: [number, number] | null;
  operations: [number, number] | null;
  modifiedBy: ReadonlyArray<string>;
  apps: ReadonlyArray<string>;
  tags: ReadonlyArray<string>;
  /** Substring search against scenario description / capabilities text. */
  capabilities: string;
  /** Notification-app slugs to match against `row.applications[*].type`.
   *  Empty array = filter inactive. Sourced from the static
   *  `notificationApps` allowlist below. */
  hasNotifications: ReadonlyArray<string>;
  /** Per-comparator date filters (last-run / scheduled / last-modified). */
  lastRun: DateFilter | null;
  scheduled: DateFilter | null;
  lastModified: DateFilter | null;
}

export const EMPTY_FILTER: ScenarioFilterValue = {
  modules: null,
  operations: null,
  modifiedBy: [],
  apps: [],
  tags: [],
  capabilities: '',
  hasNotifications: [],
  lastRun: null,
  scheduled: null,
  lastModified: null,
};

/**
 * Static allowlist of app slugs that produce notifications in Fusion.
 * Mirrors legacy `application.json`'s `hasNotifications: true` subset
 * — the legacy drawer presents the same closed set in its Has
 * Notifications popover. Sourced from the operator-facing
 * `data_app` slugs the engine returns on `applications[*].type`.
 */
const NOTIFICATION_APP_SLUGS: ReadonlyArray<{ slug: string; label: string }> = [
  { slug: 'email', label: 'Email' },
  { slug: 'gmail', label: 'Gmail' },
  { slug: 'office-365-email', label: 'Office 365 Email' },
  { slug: 'slack', label: 'Slack' },
  { slug: 'microsoft-teams', label: 'Microsoft Teams' },
  { slug: 'webhooks', label: 'Webhooks' },
];

interface ScenarioFilterDrawerProps {
  value: ScenarioFilterValue;
  onChange: (next: ScenarioFilterValue) => void;
  /** Rows from the engine — used to derive the option lists +
   *  min/max bounds for the sliders. */
  rows: ReadonlyArray<ScenarioRow>;
}

export function ScenarioFilterDrawer({
  value,
  onChange,
  rows,
}: ScenarioFilterDrawerProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<ScenarioFilterValue>(value);

  const bounds = useMemo(() => deriveBounds(rows), [rows]);
  const optionLists = useMemo(() => deriveOptions(rows), [rows]);
  const activeCount = countActive(value);

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) setDraft(value);
      }}
    >
      <SheetTrigger asChild>
        <button
          type="button"
          aria-label="Open filters"
          title="Filters"
          className={cn(
            'relative inline-flex h-9 items-center justify-center rounded-md border border-foreground/15 px-2 text-foreground/70 transition-colors hover:border-foreground/40 hover:text-foreground',
            activeCount > 0 && 'border-[color:var(--brand-yellow)] text-foreground',
          )}
        >
          <Filter className="h-4 w-4" />
          {activeCount > 0 ? (
            <span className="ml-1.5 rounded bg-[color:var(--brand-yellow)] px-1 text-xs font-semibold text-[color:var(--gradient-end)]">
              {activeCount}
            </span>
          ) : null}
        </button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="w-[420px] border-l-white/10 bg-[color:var(--surface-overlay)] text-foreground sm:max-w-[420px]"
      >
        <SheetHeader>
          <SheetTitle className="text-foreground">Filters</SheetTitle>
          <SheetDescription className="text-foreground/60">
            Narrow the scenarios list. Applied filters survive role-tab + folder changes until you
            clear them.
          </SheetDescription>
        </SheetHeader>

        <div className="max-h-[calc(100vh-200px)] space-y-6 overflow-y-auto py-4">
          <RangeFilter
            label="Modules"
            value={draft.modules ?? [bounds.modulesMin, bounds.modulesMax]}
            min={bounds.modulesMin}
            max={bounds.modulesMax}
            step={1}
            onChange={(next) =>
              setDraft({
                ...draft,
                modules: rangeIsBoundary(next, bounds.modulesMin, bounds.modulesMax) ? null : next,
              })
            }
          />

          <RangeFilter
            label="Operations (last run)"
            value={draft.operations ?? [bounds.opsMin, bounds.opsMax]}
            min={bounds.opsMin}
            max={bounds.opsMax}
            step={Math.max(1, Math.round((bounds.opsMax - bounds.opsMin) / 100))}
            onChange={(next) =>
              setDraft({
                ...draft,
                operations: rangeIsBoundary(next, bounds.opsMin, bounds.opsMax) ? null : next,
              })
            }
          />

          <MultiSelect
            label="Modified by"
            options={optionLists.modifiedBy}
            selected={draft.modifiedBy}
            onChange={(next) => setDraft({ ...draft, modifiedBy: next })}
            emptyLabel="No modifiedBy info available."
          />

          <MultiSelect
            label="Applications"
            options={optionLists.apps}
            selected={draft.apps}
            onChange={(next) => setDraft({ ...draft, apps: next })}
            emptyLabel="No application info available."
          />

          <MultiSelect
            label="Tags"
            options={optionLists.tags}
            selected={draft.tags}
            onChange={(next) => setDraft({ ...draft, tags: next })}
            renderLabel={(id) => optionLists.tagTitleById.get(id) ?? id}
            emptyLabel="No tags on the loaded scenarios."
          />

          <CapabilitiesFilter
            value={draft.capabilities}
            onChange={(v) => setDraft({ ...draft, capabilities: v })}
          />

          <NotificationsFilter
            value={draft.hasNotifications}
            onChange={(next) => setDraft({ ...draft, hasNotifications: next })}
          />

          <DateFilterField
            label="Last run"
            value={draft.lastRun}
            onChange={(next) => setDraft({ ...draft, lastRun: next })}
          />
          <DateFilterField
            label="Scheduled"
            value={draft.scheduled}
            onChange={(next) => setDraft({ ...draft, scheduled: next })}
          />
          <DateFilterField
            label="Last modified"
            value={draft.lastModified}
            onChange={(next) => setDraft({ ...draft, lastModified: next })}
          />
        </div>

        <SheetFooter className="flex flex-row items-center justify-between gap-2 border-t border-foreground/10 pt-3">
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setDraft(EMPTY_FILTER);
            }}
            className="text-foreground/70 hover:bg-foreground/10 hover:text-foreground"
          >
            <X className="mr-1.5 h-4 w-4" /> Clear all
          </Button>
          <div className="flex gap-2">
            <SheetClose asChild>
              <Button
                type="button"
                variant="outline"
                className="border-foreground/15 text-foreground/70"
              >
                Cancel
              </Button>
            </SheetClose>
            <Button
              type="button"
              onClick={() => {
                onChange(draft);
                setOpen(false);
              }}
              className="bg-[color:var(--brand-yellow)] text-[color:var(--gradient-end)] hover:opacity-90"
            >
              Apply
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function RangeFilter({
  label,
  value,
  min,
  max,
  step,
  onChange,
}: {
  label: string;
  value: [number, number];
  min: number;
  max: number;
  step: number;
  onChange: (next: [number, number]) => void;
}): JSX.Element {
  const disabled = min === max;
  return (
    <section>
      <Label className="mb-2 flex items-center justify-between text-sm text-foreground/80">
        <span>{label}</span>
        <span className="font-mono text-xs text-foreground/60">
          {value[0]} – {value[1]}
        </span>
      </Label>
      <Slider
        min={min}
        max={Math.max(max, min + 1)}
        step={step}
        value={[value[0], value[1]]}
        disabled={disabled}
        onValueChange={(v) => {
          if (v.length >= 2) onChange([v[0] ?? min, v[1] ?? max]);
        }}
        className={disabled ? 'opacity-40' : ''}
      />
    </section>
  );
}

function MultiSelect({
  label,
  options,
  selected,
  onChange,
  emptyLabel,
  renderLabel,
}: {
  label: string;
  options: ReadonlyArray<string>;
  selected: ReadonlyArray<string>;
  onChange: (next: ReadonlyArray<string>) => void;
  emptyLabel: string;
  /** Optional display-label resolver — used by the tags column to map
   *  stable tag IDs to current titles. Defaults to identity. */
  renderLabel?: (value: string) => string;
}): JSX.Element {
  if (options.length === 0) {
    return (
      <section>
        <Label className="mb-1 text-sm text-foreground/80">{label}</Label>
        <p className="text-xs text-foreground/40">{emptyLabel}</p>
      </section>
    );
  }
  const setLookup = new Set(selected);
  return (
    <section>
      <Label className="mb-1 text-sm text-foreground/80">{label}</Label>
      <div className="flex flex-wrap gap-1.5">
        {options.map((opt) => {
          const active = setLookup.has(opt);
          const labelText = renderLabel ? renderLabel(opt) : opt;
          return (
            <button
              key={opt}
              type="button"
              onClick={() => {
                if (active) onChange(selected.filter((s) => s !== opt));
                else onChange([...selected, opt]);
              }}
              className={cn(
                'rounded-full border px-2.5 py-0.5 text-xs transition-colors',
                active
                  ? 'border-[color:var(--brand-yellow)] bg-foreground/10 text-foreground'
                  : 'border-foreground/15 bg-transparent text-foreground/70 hover:border-foreground/40 hover:text-foreground',
              )}
            >
              {labelText}
            </button>
          );
        })}
      </div>
    </section>
  );
}

function deriveBounds(rows: ReadonlyArray<ScenarioRow>): {
  modulesMin: number;
  modulesMax: number;
  opsMin: number;
  opsMax: number;
} {
  let modulesMax = 0;
  let opsMax = 0;
  for (const r of rows) {
    if (typeof r.totalModules === 'number' && r.totalModules > modulesMax)
      modulesMax = r.totalModules;
    const ops = Number(r.lastSuccessfulRunOperations ?? 0);
    if (Number.isFinite(ops) && ops > opsMax) opsMax = ops;
  }
  return {
    modulesMin: 0,
    modulesMax: Math.max(modulesMax, 1),
    opsMin: 0,
    opsMax: Math.max(opsMax, 1),
  };
}

function deriveOptions(rows: ReadonlyArray<ScenarioRow>): {
  modifiedBy: ReadonlyArray<string>;
  apps: ReadonlyArray<string>;
  /** Tag IDs the operator can filter on, sorted by current title. */
  tags: ReadonlyArray<string>;
  /** Stable id→title map; the filter value stores IDs so a rename
   *  in the governance page doesn't drop the user's selection. */
  tagTitleById: Map<string, string>;
} {
  const modifiedBy = new Set<string>();
  const apps = new Set<string>();
  const tagTitleById = new Map<string, string>();
  for (const r of rows) {
    if (r.modifiedBy) modifiedBy.add(r.modifiedBy);
    for (const a of r.applications ?? []) {
      const key = a.label ?? a.type;
      if (key) apps.add(key);
    }
    for (const t of r.tags ?? []) {
      if (typeof t.id === 'number' && t.title) {
        tagTitleById.set(String(t.id), t.title);
      }
    }
  }
  const tagIds = [...tagTitleById.entries()]
    .sort((a, b) => a[1].localeCompare(b[1]))
    .map(([id]) => id);
  return {
    modifiedBy: [...modifiedBy].sort((a, b) => a.localeCompare(b)),
    apps: [...apps].sort((a, b) => a.localeCompare(b)),
    tags: tagIds,
    tagTitleById,
  };
}

function rangeIsBoundary(range: [number, number], min: number, max: number): boolean {
  return range[0] <= min && range[1] >= max;
}

function countActive(value: ScenarioFilterValue): number {
  let n = 0;
  if (value.modules) n += 1;
  if (value.operations) n += 1;
  if (value.modifiedBy.length > 0) n += 1;
  if (value.apps.length > 0) n += 1;
  if (value.tags.length > 0) n += 1;
  if (value.capabilities.trim().length > 0) n += 1;
  if (value.hasNotifications.length > 0) n += 1;
  if (value.lastRun) n += 1;
  if (value.scheduled) n += 1;
  if (value.lastModified) n += 1;
  return n;
}

/* ── Capabilities (free-text search against description) ── */
function CapabilitiesFilter({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}): JSX.Element {
  // Debounce so each keystroke doesn't trigger a parent re-render.
  // Mirrors legacy's 300ms debounce on the same control.
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  useEffect(() => {
    const id = window.setTimeout(() => {
      if (draft !== value) onChange(draft);
    }, 300);
    return () => window.clearTimeout(id);
  }, [draft, value, onChange]);
  return (
    <section>
      <Label className="mb-1 text-sm text-foreground/80">Capabilities</Label>
      <Textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="Search by scenario capabilities / description…"
        rows={3}
        className="border-foreground/15 bg-foreground/[0.04] text-foreground placeholder:text-foreground/30 focus-visible:ring-[color:var(--brand-yellow)]"
      />
    </section>
  );
}

/* ── Has Notifications (app-subset filter) ── */
function NotificationsFilter({
  value,
  onChange,
}: {
  value: ReadonlyArray<string>;
  onChange: (next: ReadonlyArray<string>) => void;
}): JSX.Element {
  const setLookup = new Set(value);
  return (
    <section>
      <Label className="mb-1 text-sm text-foreground/80">Has notifications</Label>
      <p className="mb-2 text-xs text-foreground/40">
        Show scenarios using at least one of these notification apps.
      </p>
      <div className="flex flex-wrap gap-1.5">
        {NOTIFICATION_APP_SLUGS.map(({ slug, label }) => {
          const active = setLookup.has(slug);
          return (
            <button
              key={slug}
              type="button"
              onClick={() => {
                if (active) onChange(value.filter((s) => s !== slug));
                else onChange([...value, slug]);
              }}
              className={cn(
                'rounded-full border px-2.5 py-0.5 text-xs transition-colors',
                active
                  ? 'border-[color:var(--brand-yellow)] bg-foreground/10 text-foreground'
                  : 'border-foreground/15 bg-transparent text-foreground/70 hover:border-foreground/40 hover:text-foreground',
              )}
            >
              {label}
            </button>
          );
        })}
      </div>
    </section>
  );
}

/* ── Date comparator filter (Before / On / After + Calendar) ── */
function DateFilterField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: DateFilter | null;
  onChange: (next: DateFilter | null) => void;
}): JSX.Element {
  const enabled = value !== null;
  const comparator = value?.comparator ?? 'on';
  const dateStr = value?.date ?? '';
  const selectedDate = dateStr ? new Date(`${dateStr}T00:00:00Z`) : undefined;
  const [open, setOpen] = useState(false);

  const setEnabled = (next: boolean): void => {
    if (next) {
      const today = new Date().toISOString().slice(0, 10);
      onChange({ comparator: 'on', date: dateStr || today });
    } else {
      onChange(null);
    }
  };
  const setComparator = (c: DateComparator): void => {
    if (value) onChange({ ...value, comparator: c });
  };
  const setDate = (d: Date | undefined): void => {
    if (!d) return;
    // Use UTC components so the picker rendered in any local TZ
    // produces the same calendar day on every machine.
    const yyyy = d.getUTCFullYear();
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    onChange({ comparator, date: `${yyyy}-${mm}-${dd}` });
    setOpen(false);
  };

  return (
    <section>
      <Label className="mb-2 flex items-center justify-between text-sm text-foreground/80">
        <span>{label}</span>
        <Switch checked={enabled} onCheckedChange={setEnabled} />
      </Label>
      {enabled ? (
        <>
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                className="w-full justify-start border-foreground/15 bg-foreground/[0.04] text-left text-foreground/80 hover:border-foreground/40 hover:text-foreground"
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {dateStr || 'Pick a date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={selectedDate} onSelect={setDate} initialFocus />
            </PopoverContent>
          </Popover>
          <div className="mt-2 flex">
            {(['before', 'on', 'after'] as const).map((c) => {
              const active = comparator === c;
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => setComparator(c)}
                  className={cn(
                    'flex-1 border border-foreground/15 px-2 py-1 text-xs capitalize transition-colors first:rounded-l-md last:rounded-r-md',
                    active
                      ? 'border-[color:var(--brand-yellow)] bg-foreground/10 text-foreground'
                      : 'text-foreground/70 hover:border-foreground/40 hover:text-foreground',
                  )}
                >
                  {c}
                </button>
              );
            })}
          </div>
        </>
      ) : null}
    </section>
  );
}

/**
 * Encode the filter value into a URL-safe string. We base64url the
 * JSON so the param stays compact and the URL is share-ready without
 * a long `?modifiedBy=&apps=…` chain. Empty filters return an empty
 * string so the caller can drop the param from the URL.
 */
export function encodeScenarioFilter(filter: ScenarioFilterValue): string {
  const trimmed: Partial<ScenarioFilterValue> = {};
  if (filter.modules) trimmed.modules = filter.modules;
  if (filter.operations) trimmed.operations = filter.operations;
  if (filter.modifiedBy.length > 0) trimmed.modifiedBy = filter.modifiedBy;
  if (filter.apps.length > 0) trimmed.apps = filter.apps;
  if (filter.tags.length > 0) trimmed.tags = filter.tags;
  if (filter.capabilities.trim().length > 0) trimmed.capabilities = filter.capabilities.trim();
  if (filter.hasNotifications.length > 0) trimmed.hasNotifications = filter.hasNotifications;
  if (filter.lastRun) trimmed.lastRun = filter.lastRun;
  if (filter.scheduled) trimmed.scheduled = filter.scheduled;
  if (filter.lastModified) trimmed.lastModified = filter.lastModified;
  if (Object.keys(trimmed).length === 0) return '';
  // btoa is UTF-16 only; encodeURIComponent → unescape pairs to ASCII
  // first. (filter values are all ASCII today, but tags + modifiedBy
  // can carry user-supplied names — Cyrillic, accents, etc.)
  const json = JSON.stringify(trimmed);
  const bytes = new TextEncoder().encode(json);
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Decode a base64url-encoded filter back into a `ScenarioFilterValue`.
 * Defensive — bad input returns `EMPTY_FILTER` so a corrupted share
 * link doesn't break the page.
 */
export function decodeScenarioFilter(encoded: string | null | undefined): ScenarioFilterValue {
  if (!encoded) return EMPTY_FILTER;
  try {
    const padded = encoded
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(Math.ceil(encoded.length / 4) * 4, '=');
    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const json = new TextDecoder().decode(bytes);
    const parsed = JSON.parse(json) as Record<string, unknown>;
    return {
      modules: parseRange(parsed.modules),
      operations: parseRange(parsed.operations),
      modifiedBy: parseStringArray(parsed.modifiedBy),
      apps: parseStringArray(parsed.apps),
      tags: parseStringArray(parsed.tags),
      capabilities: typeof parsed.capabilities === 'string' ? parsed.capabilities : '',
      hasNotifications: parseStringArray(parsed.hasNotifications),
      lastRun: parseDateFilter(parsed.lastRun),
      scheduled: parseDateFilter(parsed.scheduled),
      lastModified: parseDateFilter(parsed.lastModified),
    };
  } catch {
    return EMPTY_FILTER;
  }
}

function parseDateFilter(value: unknown): DateFilter | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as { comparator?: unknown; date?: unknown };
  if (
    (v.comparator !== 'before' && v.comparator !== 'on' && v.comparator !== 'after') ||
    typeof v.date !== 'string' ||
    !/^\d{4}-\d{2}-\d{2}$/.test(v.date)
  ) {
    return null;
  }
  return { comparator: v.comparator, date: v.date };
}

function parseRange(value: unknown): [number, number] | null {
  if (!Array.isArray(value) || value.length !== 2) return null;
  const [a, b] = value;
  if (typeof a !== 'number' || typeof b !== 'number') return null;
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  return [a, b];
}

function parseStringArray(value: unknown): ReadonlyArray<string> {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === 'string');
}

/**
 * Apply a `ScenarioFilterValue` to a row list. Pure; no side effects.
 */
export function applyScenarioFilter(
  rows: ReadonlyArray<ScenarioRow>,
  filter: ScenarioFilterValue,
): ReadonlyArray<ScenarioRow> {
  return rows.filter((row) => {
    if (filter.modules) {
      const m = typeof row.totalModules === 'number' ? row.totalModules : 0;
      if (m < filter.modules[0] || m > filter.modules[1]) return false;
    }
    if (filter.operations) {
      const o = Number(row.lastSuccessfulRunOperations ?? 0);
      if (!Number.isFinite(o) || o < filter.operations[0] || o > filter.operations[1]) return false;
    }
    if (filter.modifiedBy.length > 0) {
      if (!row.modifiedBy || !filter.modifiedBy.includes(row.modifiedBy)) return false;
    }
    if (filter.apps.length > 0) {
      const rowApps = (row.applications ?? []).map((a) => a.label ?? a.type ?? '');
      if (!filter.apps.some((a) => rowApps.includes(a))) return false;
    }
    if (filter.tags.length > 0) {
      // Match by stable tag ID so a rename in the governance page
      // doesn't drop the user's filter. Older filter values that still
      // carry titles fall back to title match for one render — the
      // next time the drawer is opened, options will refresh to IDs.
      const rowTagIds = (row.tags ?? []).map((t) => String(t.id));
      const rowTagTitles = (row.tags ?? []).map((t) => t.title);
      if (!filter.tags.some((t) => rowTagIds.includes(t) || rowTagTitles.includes(t))) {
        return false;
      }
    }
    if (filter.capabilities.trim().length > 0) {
      const needle = filter.capabilities.trim().toLowerCase();
      // `aiTechnicalSummary` was previously in the search corpus but
      // no longer ships on the list response (payload size — see
      // `hasAiTechnicalSummary` doc). Search now covers the business
      // summary instead; the technical summary is fetched lazily for
      // single-scenario display only.
      const hay =
        `${row.description ?? ''} ${row.aiBusinessSummary ?? ''} ${row.name}`.toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    if (filter.hasNotifications.length > 0) {
      const rowAppTypes = (row.applications ?? [])
        .map((a) => (a.type ?? '').toLowerCase())
        .filter(Boolean);
      const needles = filter.hasNotifications.map((s) => s.toLowerCase());
      if (!needles.some((n) => rowAppTypes.includes(n))) return false;
    }
    if (filter.lastRun && !matchesDateFilter(row.lastSuccessfulRunAt, filter.lastRun)) return false;
    if (filter.lastModified && !matchesDateFilter(row.modifiedAt, filter.lastModified))
      return false;
    if (
      filter.scheduled &&
      !matchesDateFilter(extractScheduleDate(row.eventSchedule), filter.scheduled)
    )
      return false;
    return true;
  });
}

/**
 * Compare an ISO-or-null timestamp against a `{ comparator, date }`
 * filter. The filter's `date` is calendar-day-only (YYYY-MM-DD) per
 * the picker's contract — we normalize both sides to UTC midnight
 * so DST + local-TZ jitter never flips a row in or out by a day.
 *
 *   - `before`: row date strictly < filter date (start of day)
 *   - `on`:     row date within [filter date, filter date + 1 day)
 *   - `after`:  row date strictly >= filter date + 1 day
 *
 * Null row values fail every comparator (filters are inclusive of
 * presence — "before X" doesn't mean "missing or before X").
 */
function matchesDateFilter(rowDate: string | null | undefined, filter: DateFilter): boolean {
  if (!rowDate) return false;
  const row = Date.parse(rowDate);
  if (!Number.isFinite(row)) return false;
  const dayStart = Date.parse(`${filter.date}T00:00:00Z`);
  const dayEnd = dayStart + 24 * 60 * 60 * 1000;
  if (filter.comparator === 'before') return row < dayStart;
  if (filter.comparator === 'after') return row >= dayEnd;
  return row >= dayStart && row < dayEnd;
}

/**
 * Pull a comparable ISO date string out of the row's `eventSchedule`
 * blob. The shape varies by schedule type — `daily` carries `time`
 * only, `once` carries `date + time`, etc. We return the first ISO
 * string we can construct, or `null` when the schedule is recurring
 * without a fixed date (matches the legacy behavior: recurring
 * schedules don't participate in the "Scheduled before/on/after"
 * filter).
 */
function extractScheduleDate(schedule: ScenarioRow['eventSchedule']): string | null {
  if (!schedule) return null;
  const date = schedule.date;
  const time = schedule.time;
  if (typeof date !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  return typeof time === 'string' ? `${date}T${time}Z` : `${date}T00:00:00Z`;
}
